import { useEffect, useState, type ReactNode } from "react";
import type { Api } from "../../shared/api";
import { APP_TOOLKITS } from "../../shared/api";
import type { ConnectionsView } from "../../shared/contracts";
import { IcloudSection } from "../components/icloud-card";
import { IconCheck, IconExternal } from "../components/icons";
import { Button, buttonClass, CopyButton, ErrorNotice, Loading, Notice, PageTop, RowList, Section } from "../components/ui";
import { api, errorText } from "../lib/api";
import { fmtRelative, prettyPhone } from "../lib/format";
import { useNow, usePageTitle } from "../lib/hooks";
import { currentPushState, disablePush, enablePush, isIosBrowserTab, type PushState } from "../lib/push";
import { useMe } from "../lib/session";
import { invalidate, useResource } from "../lib/store";

type AppsRes = Api["GET /api/apps"]["res"];

function isHttpsUrl(url: string, hosts?: string[]): boolean {
  try {
    const u = new URL(url);
    return u.protocol === "https:" && (!hosts || hosts.includes(u.hostname));
  } catch {
    return false;
  }
}

function isSmsUri(uri: string): boolean {
  return /^sms:[+0-9]/i.test(uri);
}

/** « Relié »: a light disc with a check, and the word. */
function Linked({ label = "Relié" }: { label?: string }) {
  return (
    <span className="cv-pop inline-flex shrink-0 items-center gap-1.5 text-[13px] text-muted">
      <span aria-hidden="true" className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-ink text-ink-fg">
        <IconCheck width={10} height={10} strokeWidth={3.6} />
      </span>
      {label}
    </span>
  );
}

/** One row of a panel (V2Connexions): a lettered tile, the name, one faint line, « Relié » or one button; below, what it needs. */
function ChannelCard({ mono, title, line, status, action, children }: { mono: string; title: string; line?: ReactNode; status?: string | null; action?: ReactNode; children?: ReactNode }) {
  return (
    <div className="min-w-0 py-[11px]">
      <div className="flex min-w-0 items-center gap-3">
        <span aria-hidden="true" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[11px] bg-inset font-display text-[17px]">
          {mono}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-[15px] font-semibold">{title}</h3>
          {line ? <p className="truncate text-[12.5px] text-subtle">{line}</p> : null}
        </div>
        {status ? <Linked label={status} /> : null}
        {action}
      </div>
      {children ? <div className="mt-2.5 flex flex-col gap-2 pl-12">{children}</div> : null}
    </div>
  );
}

const CARD_BUTTON = "md:h-8 md:px-3.5";
const QUIET_BUTTON = "inline-flex min-h-11 items-center self-start text-[13px] text-subtle underline underline-offset-[3px] hover:text-fg sm:min-h-8";

/** /connexions — where to write to the agent (iMessage, e-mail, Telegram, notifications), and what it may look at. */
export function ConnectionsPage({ returnedApp }: { returnedApp?: string }) {
  usePageTitle("Connexions");
  const conn = useResource<ConnectionsView>("connections", (signal) => api("GET /api/connections", { signal }));
  const me = useMe();

  if (conn.loading) return <Loading />;
  if (!conn.data) return <ErrorNotice message={conn.error?.message ?? "Impossible de charger tes connexions."} onRetry={() => void conn.reload()} />;
  const c = conn.data;

  return (
    <div className="mx-auto flex max-w-[620px] flex-col gap-6">
      <PageTop title="Connexions" className="mb-0" />

      <section aria-labelledby="c-write" className="flex flex-col gap-2">
        <h2 id="c-write" className="px-1 text-[13px] font-semibold text-subtle">
          Où lui écrire
        </h2>
        <RowList>
          <IMessageCard data={c.imessage} onChange={conn.reload} />
          <TelegramCard data={c.telegram} onChange={conn.reload} />
          <ChannelCard
            mono="@"
            title="E-mail"
            line={c.email.available && c.email.address ? c.email.address : "Pas activé ici"}
            action={c.email.available && c.email.address ? <CopyButton text={c.email.address} label="Copier" className={CARD_BUTTON} /> : null}
          >
            {c.email.available && c.email.address && me?.email ? <p className="text-[12.5px] text-subtle wrap-anywhere">Lu seulement depuis {me.email}.</p> : null}
          </ChannelCard>
          <PushCard data={c.push} onChange={conn.reload} />
        </RowList>
      </section>

      <AppsSection returnedApp={returnedApp} onChange={conn.reload} icloud={c.icloud ? <IcloudSection data={c.icloud} onChange={conn.reload} /> : null} />
      {me?.role === "owner" ? <OwnerSetup /> : null}
    </div>
  );
}

function IMessageCard({ data, onChange }: { data: ConnectionsView["imessage"]; onChange: () => unknown }) {
  const now = useNow(15_000);
  const [code, setCode] = useState<Api["POST /api/connections/imessage/code"]["res"] | null>(null);
  const [busy, setBusy] = useState<"code" | "unlink" | null>(null);
  const [error, setError] = useState<string | null>(null);

  // While a code is waiting to be texted, poll until the phone shows up as linked.
  useEffect(() => {
    if (!code || data.linked) return;
    const t = setInterval(onChange, 5000);
    return () => clearInterval(t);
  }, [code, data.linked, onChange]);

  async function requestCode() {
    setBusy("code");
    setError(null);
    try {
      setCode(await api("POST /api/connections/imessage/code", { body: {} }));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  async function unlink() {
    setBusy("unlink");
    setError(null);
    try {
      await api("DELETE /api/connections/imessage");
      setCode(null);
      onChange();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <ChannelCard
      mono="i"
      title="iMessage"
      line={!data.available ? "Pas activé ici" : data.linked ? prettyPhone(data.linked) : "Ton numéro"}
      status={data.linked ? "Relié" : null}
      action={
        data.available && !data.linked && !(code && code.expiresAt > now) ? (
          <Button size="sm" className={CARD_BUTTON} busy={busy === "code"} onClick={() => void requestCode()}>
            Relier
          </Button>
        ) : null
      }
    >
      {data.available && data.linked ? (
        <>
          {data.number ? <p className="text-[12.5px] text-subtle">Il t'écrit depuis le {prettyPhone(data.number)}.</p> : null}
          <button type="button" disabled={busy !== null} onClick={() => void unlink()} className={QUIET_BUTTON}>
            Délier ce numéro
          </button>
        </>
      ) : data.available && code && code.expiresAt > now ? (
        <>
          <p className="text-[13.5px] text-muted">
            Envoie ce code au <span className="font-medium text-fg">{prettyPhone(code.number)}</span> :
          </p>
          <p className="font-mono text-[24px] font-semibold tracking-[0.2em]">{code.code}</p>
          <p className="text-[12.5px] text-subtle">Expire {fmtRelative(code.expiresAt, now)}.</p>
          <div className="flex flex-wrap gap-2">
            {isSmsUri(code.smsUri) ? (
              <a href={code.smsUri} className={buttonClass("primary", "sm")}>
                Envoyer le code
              </a>
            ) : null}
            <CopyButton text={code.code} label="Copier le code" />
          </div>
        </>
      ) : null}
      {error ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}
    </ChannelCard>
  );
}

function TelegramCard({ data, onChange }: { data: ConnectionsView["telegram"]; onChange: () => unknown }) {
  const [link, setLink] = useState<string | null>(null);
  const [busy, setBusy] = useState<"link" | "unlink" | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!link || data.linked) return;
    const t = setInterval(onChange, 5000);
    return () => clearInterval(t);
  }, [link, data.linked, onChange]);

  async function start() {
    setBusy("link");
    setError(null);
    try {
      const res = await api("POST /api/connections/telegram/link", { body: {} });
      if (!isHttpsUrl(res.url, ["t.me", "telegram.me"])) throw new Error("bad url");
      setLink(res.url);
      window.open(res.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  async function unlink() {
    setBusy("unlink");
    setError(null);
    try {
      await api("DELETE /api/connections/telegram");
      setLink(null);
      onChange();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <ChannelCard
      mono="T"
      title="Telegram"
      line={!data.available ? "Pas activé ici" : data.linked ? (data.botUsername ? `@${data.botUsername}` : "Relié") : "En un clic"}
      status={data.linked ? "Relié" : null}
      action={
        data.available && !data.linked ? (
          <Button size="sm" className={CARD_BUTTON} busy={busy === "link"} onClick={() => void start()}>
            Relier
          </Button>
        ) : null
      }
    >
      {data.available && data.linked ? (
        <button type="button" disabled={busy !== null} onClick={() => void unlink()} className={QUIET_BUTTON}>
          Délier Telegram
        </button>
      ) : link ? (
        <a href={link} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center gap-1 text-[13px] text-muted hover:text-fg sm:min-h-0">
          Puis « Démarrer » dans Telegram <IconExternal width={13} height={13} />
        </a>
      ) : null}
      {error ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}
    </ChannelCard>
  );
}

/** One row of « Ce qu'il peut regarder »: a lettered tile, the app, one line, one action. */
export function AppRow({ letter, name, line, action, children }: { letter: string; tint?: number; name: string; line: string; action: ReactNode; children?: ReactNode }) {
  return (
    <div className="py-[11px]">
      <div className="flex min-w-0 items-center gap-3">
        <span aria-hidden="true" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[11px] bg-inset font-display text-[17px]">
          {letter}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-semibold">{name}</p>
          <p className="truncate text-[12.5px] text-subtle">{line}</p>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function AppsSection({ returnedApp, onChange, icloud }: { returnedApp?: string; onChange: () => unknown; icloud: ReactNode }) {
  const apps = useResource<AppsRes>("apps", (signal) => api("GET /api/apps", { signal }));
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function connect(slug: string) {
    setBusy(slug);
    setError(null);
    try {
      const { redirectUrl } = await api("POST /api/apps/:toolkit/link", { params: { toolkit: slug }, body: {} });
      if (!isHttpsUrl(redirectUrl)) throw new Error("bad url");
      window.location.assign(redirectUrl);
    } catch (err) {
      setError(errorText(err));
      setBusy(null);
    }
  }

  const toolkits = apps.data?.toolkits ?? APP_TOOLKITS.map((t) => ({ ...t, connected: false, status: null }));
  const returned = returnedApp ? toolkits.find((t) => t.slug === returnedApp) : undefined;
  // Connected first.
  const sorted = [...toolkits].sort((a, b) => Number(b.connected) - Number(a.connected));

  // Coming back from the provider's consent screen: the connection may take a moment to become active.
  useEffect(() => {
    if (!returnedApp) return;
    invalidate("apps");
    onChange();
  }, [returnedApp, onChange]);

  return (
    <section aria-labelledby="c-look" className="flex flex-col gap-2">
      <h2 id="c-look" className="px-1 text-[13px] font-semibold text-subtle">
        Ce qu'il peut regarder
      </h2>
      {returned ? (
        <Notice tone={returned.connected ? "success" : "info"} role="status">
          {returned.connected ? `${returned.name} est connecté.` : `Connexion de ${returned.name} en cours…`}
        </Notice>
      ) : null}
      <RowList>
        {apps.loading ? (
          <Loading />
        ) : apps.data && !apps.data.available ? null : (
          sorted.map((t, i) => (
            <AppRow
              key={t.slug}
              letter={t.name.charAt(0)}
              tint={i}
              name={t.name}
              line={t.connected ? "Il peut regarder" : t.status === "expired" ? "Connexion expirée" : t.status === "failed" ? "Échec de connexion" : "Non connecté"}
              action={
                t.connected ? (
                  <Linked label="Connecté" />
                ) : (
                  <Button size="sm" busy={busy === t.slug} disabled={busy !== null} onClick={() => void connect(t.slug)} aria-label={`Connecter ${t.name}`} className={CARD_BUTTON}>
                    Connecter
                  </Button>
                )
              }
            />
          ))
        )}
        {icloud}
      </RowList>
      {apps.error && !apps.data ? <ErrorNotice message={apps.error.message} onRetry={() => void apps.reload()} /> : null}
      {error ? (
        <Notice tone="danger" role="alert" className="mt-3">
          {error}
        </Notice>
      ) : null}
    </section>
  );
}

function PushCard({ data, onChange }: { data: ConnectionsView["push"]; onChange: () => unknown }) {
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    currentPushState()
      .then((s) => {
        if (!cancelled) setState(s);
      })
      .catch(() => {
        if (!cancelled) setState("unsupported");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function enable() {
    setBusy(true);
    setError(null);
    try {
      const key = data.publicKey ?? (await api("GET /api/push/key")).publicKey;
      if (!key) {
        setError("Les notifications ne sont pas configurées sur ce serveur.");
        return;
      }
      const next = await enablePush(key);
      setState(next);
      if (next === "denied") setError("Bloquées : autorise-les dans les réglages du navigateur.");
      onChange();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    setBusy(true);
    setError(null);
    try {
      await disablePush();
      setState("default");
      onChange();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const ios = typeof navigator !== "undefined" && isIosBrowserTab();
  const device = typeof navigator !== "undefined" && /Mac/.test(navigator.platform) ? "ce Mac" : "cet appareil";

  return (
    <ChannelCard
      mono="N"
      title="Notifications"
      line={
        !data.available
          ? "Pas configurées ici"
          : state === "enabled"
            ? `Sur ${device}${data.subscriptions > 1 ? ` et ${data.subscriptions - 1} autre${data.subscriptions > 2 ? "s" : ""}` : ""}`
            : `Sur ${device}`
      }
      status={state === "enabled" ? "Activées" : null}
      action={
        data.available && state !== "enabled" ? (
          <Button size="sm" className={CARD_BUTTON} busy={busy} disabled={state === "unsupported" || state === "needs-install"} onClick={() => void enable()} aria-label={`Activer sur ${device}`}>
            Activer
          </Button>
        ) : null
      }
    >
      {data.available && state === "enabled" ? (
        <button type="button" disabled={busy} onClick={() => void disable()} className={QUIET_BUTTON}>
          Désactiver ici
        </button>
      ) : data.available ? (
        <>
          {state === "needs-install" || ios ? <p className="text-[12.5px] text-subtle">Sur iPhone : Partager, puis « Sur l'écran d'accueil ».</p> : null}
          {state === "denied" ? <p className="text-[12.5px] text-warning">Bloquées dans le navigateur.</p> : null}
          {state === "unsupported" ? <p className="text-[12.5px] text-subtle">Ce navigateur ne les gère pas.</p> : null}
        </>
      ) : null}
      {error ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}
    </ChannelCard>
  );
}

/** Owner only: one-time setup of the provider webhooks for this deployment. */
function OwnerSetup() {
  const [busy, setBusy] = useState<"telegram" | "composio" | null>(null);
  const [message, setMessage] = useState<{ tone: "success" | "danger"; text: string } | null>(null);

  async function run(what: "telegram" | "composio") {
    setBusy(what);
    setMessage(null);
    try {
      if (what === "telegram") {
        const res = await api("POST /api/connections/telegram/webhook", { body: {} });
        setMessage({ tone: "success", text: `Webhook Telegram enregistré sur ${res.url}.` });
      } else {
        const res = await api("POST /api/apps/admin/subscribe", { body: {} });
        setMessage({
          tone: res.pointsHere ? "success" : "danger",
          text: res.pointsHere ? `Webhook des apps ${res.created ? "créé" : "à jour"} (${res.webhookUrl}).` : `Le webhook des apps pointe ailleurs : ${res.webhookUrl}.`,
        });
      }
    } catch (err) {
      setMessage({ tone: "danger", text: errorText(err) });
    } finally {
      setBusy(null);
    }
  }

  return (
    <Section title="Administration">
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" size="sm" busy={busy === "telegram"} disabled={busy !== null} onClick={() => void run("telegram")}>
          Enregistrer le webhook Telegram
        </Button>
        <Button variant="secondary" size="sm" busy={busy === "composio"} disabled={busy !== null} onClick={() => void run("composio")}>
          Enregistrer le webhook des apps
        </Button>
      </div>
      {message ? (
        <Notice tone={message.tone} role="status" className="mt-3">
          {message.text}
        </Notice>
      ) : null}
    </Section>
  );
}
